import { getVerificationUrl, getSignupUrl, getUserUrl, getResetCodeUrl, getResetUrl, getLoginUrl, getLogoutUrl, getUpdateMailUrl, getVerificationCodeUrl, getUpdatePasswordUrl, getUpdatePropicUrl, getUsersUrl } from "../endpoints";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import useAxios from "../../hooks/useAxios";
import { useContext } from "react";

export const useGetUser = (username) => {
    const { setSession } = useContext(UserContext);
    const client = useAxios();
    return useQuery(['getUser', username], () => {
        return client.get(getUserUrl(username));
    }, {
        initialData: () => {
            return {
                data: localStorage.getItem('user')?.data
            }
        },
        onSuccess: (result) => {
            if(result) setSession(result.data);
        }
    });
}

export const useGetUsers = () => {
    const client = useAxios();
    return useQuery('getUsers', () => {
        return client.get(getUsersUrl());
    });
}

export const useSignup = () => {
    const { setSession } = useContext(UserContext);
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((body) => {
        return client.put(getSignupUrl(), body);
    }, {
        onSuccess: (result) => {
            queryClient.invalidateQueries('getUser');
            setSession(result.data);
        }
    });
};

export const useLogin = () => {
    const { setSession } = useContext(UserContext);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const client = useAxios();
    return useMutation((body) => {
        return client.post(getLoginUrl(), body);
    }, {
        onSuccess: (result) => {
            queryClient.invalidateQueries('getUser');
            setSession(result.data);

            if(location.state?.redirect) {  // redirect
                navigate(location.state.redirect);
            }
            else {  // no redirect
                navigate('/account');
            }
        }
    });
};

export const useLogout = () => {
    const { destroySession } = useContext(UserContext);
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation(() => {
        destroySession();
        return client.post(getLogoutUrl());
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries('getUser', {
                refetchActive: false,
                refetchInactive: false
            });
            queryClient.invalidateQueries('getAddress', {
                refetchActive: false,
                refetchInactive: false
            });
        }
    });
};

export const useVerifyCode = () => {
    const client = useAxios();
    return useMutation(() => {
        return client.put(getVerificationCodeUrl());
    });
};

export const useVerifyUser = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((body) => {
        return client.patch(getVerificationUrl(), body);
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries('getUser');
        }
    });
};

export const useForgotPassword = () => {
    const client = useAxios();
    return useMutation((body) => {
        return client.put(getResetCodeUrl(), body);
    });
};

export const useResetPassword = () => {
    const client = useAxios();
    return useMutation((body) => {
        return client.patch(getResetUrl(), body);
    });
};

export const useUpdatePassword = () => {
    const client = useAxios();
    return useMutation((body) => {
        return client.patch(getUpdatePasswordUrl(), body);
    });
};

export const useUpdateEmail = () => {
    const queryClient = useQueryClient();
    const client = useAxios();
    return useMutation((body) => {
        return client.patch(getUpdateMailUrl(), body);
    }, {
        onMutate: async(user) => {
            await queryClient.cancelQueries('getUser');
            const previousUser = queryClient.getQueryData('getUser');
            queryClient.setQueryData('getUser', (prev) => {
                return {
                    ...prev,
                    data: {
                        ...prev.data,
                        email: user.email,
                        verified: 0
                    }
                };
            });
            return {previousUser}; // to revert in case of error
        },
        onError: (_error, _address, context) => {
            queryClient.setQueryData('getUser', context.previousUser);
        },
        onSettled: () => {
            queryClient.invalidateQueries('getUser');
        }
    });
};

export const useUpdatePropic = () => {
    const client = useAxios();
    return useMutation((body) => {
        return client.patch(getUpdatePropicUrl(), body);
    });
}